import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ModerationTransitions, ModerationTransitionConditions } from '../../../../../../models';

import { Button, SelectHierarchyLabel, Message, Section } from '@weezevent/nacre';

import { ConditionOption, SelectedCondition } from '../../components';

import css from '../transitionDetails.module.css';
import ConditionModal from '../../conditions/modal';

export default function ConditionsStep({ form, setForm, onCardDone, flowId, organization, transitionExists }) {
    const { t } = useTranslation();
    const history = useHistory();
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [querySearch, setQuerySearch] = React.useState('');
    const [conditionModal, setConditionModal] = React.useState({ open: false, id: null });

    const [transitionConditions, loadingTransitionConditions, syncTransitionConditions] = ModerationTransitionConditions.useApiModel(
        {
            organizationId: organization.id,
            flowId,
            transitionId: form.id,
            cache: false,
            allow_cache: false,
            query: {
                search: querySearch
            }
        },
        [flowId, organization.id, querySearch]
    );

    const conditions = React.useMemo(() => {
        return form.conditions.map(ac => new ModerationTransitionConditions(ac));
    }, [form.conditions]);

    const placeHolder = React.useMemo(() => {
        if (!form.conditions.length) {
            return t('ticket.common.action.select');
        }
        return t('ticket.moderation.statusesTransitions.transition.steps.conditions.selectedCondition', { count: form.conditions.length });
    }, [form.conditions]);

    const conditionsOptions = React.useCallback(
        (conditions = []) => {
            if (loadingTransitionConditions || !conditions) {
                return [];
            }

            return conditions
                ?.filter(cn => !form.conditions?.some(c => c.id === cn.id))
                .map(cnd => ({
                    key: cnd.id,
                    text: <ConditionOption condition={cnd} />,
                    value: cnd.id,
                    item: cnd
                }));
        },
        [loadingTransitionConditions, form.conditions]
    );

    const handleChange = React.useCallback(
        item => {
            setForm('conditions', [...form.conditions, item]);
        },
        [form.conditions]
    );

    const handleDelete = React.useCallback(
        pk => {
            setForm('conditions', [...form.conditions.filter(cnd => cnd.id !== pk)]);
        },
        [form.conditions]
    );

    const handleRedirect = React.useCallback(
        pk => {
            window.open(`/ticket/O${organization.id}/moderation/moderation_flows/${flowId}/statuses_transitions/conditions/${pk}`, '_blank').focus();
        },
        [organization.id, flowId]
    );

    const handleSubmit = React.useCallback(
        (isQuitting = false) => {
            let transition = new ModerationTransitions({ ...form });
            transition.actions = form.actions.map(ac => ac.id);
            transition.conditions = form.conditions.map(cnd => cnd.id);
            transition.save({ organizationId: organization.id, flowId }).catch(() => {
                setIsSubmitting(false);
                return;
            });

            if (isQuitting) {
                history.push(`/ticket/O${organization.id}/moderation/moderation_flows/${flowId}/statuses_transitions/transitions`);
            } else {
                onCardDone(true);
            }
        },
        [form, organization.id, flowId]
    );

    return (
        <>
            <Message
                type="default"
                title={t('ticket.moderation.statusesTransitions.transition.steps.conditions.title')}
                links={{
                    label: t('ticket.moderation.statusesTransitions.transition.steps.conditions.create'),
                    onClick: () => setConditionModal({ open: true, id: null })
                }}
            >
                {t('ticket.moderation.statusesTransitions.transition.steps.conditions.message')}
            </Message>
            <ConditionModal
                organization={organization}
                flowId={flowId}
                state={conditionModal}
                onClose={() => setConditionModal({ open: false, id: null })}
                sync={syncTransitionConditions}
            />
            <div className={css['selector-container']}>
                <SelectHierarchyLabel
                    height="medium"
                    placeholder={placeHolder}
                    searchValue={querySearch}
                    onExternalSearch={setQuerySearch}
                    options={conditionsOptions(transitionConditions)}
                    externalOptions={conditionsOptions(transitionConditions)}
                    placeholderSearch={t('ticket.common.action.search')}
                    onChange={(_, { item }) => handleChange(item)}
                    label={t('ticket.moderation.statusesTransitions.transition.steps.conditions.selectLabel')}
                />
            </div>

            {!!conditions.length && (
                <Section className={css['action-section']}>
                    {conditions?.map(selectedCondition => (
                        <SelectedCondition
                            key={selectedCondition.id}
                            condition={selectedCondition}
                            handleDelete={({ pk }) => handleDelete(pk)}
                            handleEdit={({ pk }) => handleRedirect(pk)}
                        />
                    ))}
                </Section>
            )}

            {!transitionExists && (
                <Button.Group>
                    <Button
                        primary
                        label={t('ticket.moderation.statusesTransitions.transition.steps.settings.saveAndContinue')}
                        onClick={() => handleSubmit()}
                        disabled={isSubmitting}
                    />
                    <Button
                        primary
                        inverted
                        label={t(`ticket.moderation.statusesTransitions.transition.steps.settings.saveAndQuit`)}
                        onClick={() => handleSubmit(true)}
                        disabled={isSubmitting}
                    />
                </Button.Group>
            )}
        </>
    );
}
